import * as React from 'react'
import { Tooltip as TooltipPrimitive } from '@base-ui/react/tooltip'

import { cn } from '@/registry/reapit/lib/utils'
import { asChildProps } from '@/registry/reapit/lib/as-child-props'

// ---------------------------------------------------------------------------
// TooltipProvider
// ---------------------------------------------------------------------------

function TooltipProvider({
  delayDuration = 0,
  children,
  ...props
}: {
  delayDuration?: number
  children?: React.ReactNode
} & Omit<React.ComponentProps<typeof TooltipPrimitive.Provider>, 'delay'>) {
  return (
    <TooltipPrimitive.Provider data-slot="tooltip-provider" delay={delayDuration} {...props}>
      {children}
    </TooltipPrimitive.Provider>
  )
}

// ---------------------------------------------------------------------------
// Tooltip (Root)
// ---------------------------------------------------------------------------

function Tooltip({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return <TooltipPrimitive.Root data-slot="tooltip" {...props} />
}

// ---------------------------------------------------------------------------
// TooltipTrigger
// ---------------------------------------------------------------------------

function TooltipTrigger({
  asChild,
  children,
  ...props
}: Omit<React.ComponentProps<typeof TooltipPrimitive.Trigger>, 'render'> & {
  asChild?: boolean
}) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...asChildProps(asChild, children)} {...props} />
}

// ---------------------------------------------------------------------------
// TooltipContent
// ---------------------------------------------------------------------------
/*
 * DS alignment (Phase 3C)
 *
 * DS specifies a dark tooltip bubble (neutral-900 background) with white text.
 * bg-foreground (= --foreground = neutral-900 = #222B33) + text-background (white)
 * satisfies this in light mode. In dark mode the inversion remains readable.
 *
 * Padding: px-3 py-1.5 — compact, matches DS anatomy.
 * Font:    text-xs (12px) — DS recommends very compact tooltip labels.
 *
 * Base UI splits positioning into a separate Positioner component between
 * Portal and Popup. We bake this structural detail into our wrapper so
 * consumers see the same `<TooltipContent side="top" sideOffset={4}>` API
 * they had with Radix.
 *
 * DS Page: 8403150
 */
function TooltipContent({
  className,
  sideOffset = 4,
  side = 'top',
  align = 'center',
  hidden,
  children,
  ...props
}: {
  className?: string
  sideOffset?: number
  side?: 'top' | 'right' | 'bottom' | 'left'
  align?: 'start' | 'center' | 'end'
  hidden?: boolean
  children?: React.ReactNode
} & Omit<React.ComponentProps<typeof TooltipPrimitive.Popup>, 'className' | 'children' | 'hidden'>) {
  if (hidden) return null
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Positioner side={side} sideOffset={sideOffset} align={align}>
        <TooltipPrimitive.Popup
          role="tooltip"
          data-slot="tooltip-content"
          className={cn(
            'z-50 w-fit max-w-xs rounded-md bg-foreground px-3 py-1.5 text-xs text-balance text-background origin-[var(--transform-origin)] transition-[opacity,scale] duration-150 scale-100 opacity-100 data-[starting-style]:scale-95 data-[starting-style]:opacity-0 data-[ending-style]:scale-95 data-[ending-style]:opacity-0',
            className,
          )}
          {...props}
        >
          {children}
          <TooltipPrimitive.Arrow className="z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px] bg-foreground fill-foreground" />
        </TooltipPrimitive.Popup>
      </TooltipPrimitive.Positioner>
    </TooltipPrimitive.Portal>
  )
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
